import { createLogic } from 'redux-logic';
import { filter, find, groupBy, includes, map, reject } from 'lodash';
import index from 'redux-ui-router';

// Action Types
const CANCEL = '@@app/filters/CANCEL';
const FETCH = '@@app/filters/FETCH';
const FETCH_SUCCESS = '@@app/filters/FETCH_SUCCESS';
const FETCH_ERROR = '@@app/filters/FETCH_ERROR';
const TOGGLE_IS_ACTIVE = '@@app/filters/TOGGLE_IS_ACTIVE';

// Initial State
export const initialState = {
  loading: false,
  isActive: false,
  message: '',
  recipes: [],
  workouts: [],
  videos: [
    { name: 'Cardio', key: 'type', value: 'cardio' },
    { name: 'Strength', key: 'type', value: 'strength' },
    { name: 'Stretch', key: 'type', value: 'stretch' },
    { name: 'Dance', key: 'type', value: 'dance' },
    { name: 'Equipment Required', key: 'equipmentRequired', value: '1' },
    { name: 'No Equipment', key: 'equipmentRequired', value: '0' },
    { name: 'Full Length', key: 'time', value: 'full length' },
    { name: 'Blaster', key: 'time', value: 'blaster' },
  ],
  serves: [
    { id: 1, name: '1 serve', type: 'Serves' },
    { id: 2, name: '2 serves', type: 'Serves' },
    { id: 3, name: '3 serves', type: 'Serves' },
    { id: 4, name: '4 serves', type: 'Serves' },
    { id: 5, name: '> 4 serves', type: 'Serves' },
  ],
  order: {
    recipes: [
      { id: 1, name: 'Our Picks', value: 'our_picks' },
      { id: 2, name: 'Popular', value: 'popular' },
      { id: 3, name: 'Name', value: 'name' },
      { id: 4, name: 'Time', value: 'time_asc' },
      { id: 5, name: 'Newest', value: 'new' },
    ],
    workouts: [
      { id: 6, name: 'Our Picks', value: 'our_picks' },
      { id: 7, name: 'Name', value: 'name' },
      { id: 8, name: 'Newest', value: 'new' },
      { id: 9, name: 'Shortest', value: 'time_asc' },
      { id: 10, name: 'Longest', value: 'time_desc' },
      { id: 11, name: 'Low Intensity', value: 'intensity_asc' },
      { id: 12, name: 'High Intensity', value: 'intensity_desc' },
    ],
    videos: [
      { id: 13, name: 'Our Picks', value: 'our_picks' },
      { id: 14, name: 'Popular', value: 'popular' },
      { id: 15, name: 'Name', value: 'name' },
      { id: 16, name: 'Newest', value: 'new' },
      { id: 17, name: 'Shortest', value: 'time_asc' },
      { id: 18, name: 'Longest', value: 'time_desc' },
    ],
  },
};

// Observables
const fetchLogic = createLogic({
  type: FETCH,
  cancelType: CANCEL,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, getState, httpClient }) {
    const { endpoint, key } = action.payload;

    return httpClient.get(endpoint).then(({ data }) => {
      return { data, key };
    });
  },
});

export const filtersLogic = [fetchLogic];

// Selectors
export const selectFilters = (state, key, checked) => {
  return state[key].map(item => setChecked(item, 'id', toArray(checked)));
};

export const selectSort = (state, checked = 'our_picks', key) => {
  return state.order[key].map(item => setChecked(item, 'value', [checked]));
};

export const selectSortByRecipes = (state, checked) => {
  return selectSort(state, checked, 'recipes');
};

export const selectSortByWorkouts = (state, checked) => {
  return selectSort(state, checked, 'workouts');
};

export const selectSortByVideos = (state, checked) => {
  return selectSort(state, checked, 'videos');
};

export const selectFiltersChecked = (state, key, filters, serves) => {
  let checked = [];

  if (filters) {
    const ids = filters.split(',').map(i => parseInt(i, 10));
    checked = reject(state[key], item => ids.indexOf(item.id) < 0);
  }

  if (serves) {
    const serving = find(state.serves, ['id', parseInt(serves)]);
    checked.push(serving);
  }

  return checked;
};

export const selectFiltersRecipes = (state, checked) => {
  return selectFilters(state, 'recipes', checked);
};

export const selectFiltersRecipesChecked = (state, filters, serves) => {
  return selectFiltersChecked(state, 'recipes', filters, serves);
};

export const selectFiltersRecipesGrouped = (
  state,
  checkedRecipes,
  checkedServes
) => {
  return {
    ...groupBy(selectFiltersRecipes(state, checkedRecipes), 'type'),
    Serves: selectFiltersServes(state, checkedServes),
  };
};

export const selectFiltersServes = (state, checked) => {
  return selectFilters(state, 'serves', checked);
};

export const selectFiltersWorkouts = (state, checked) => {
  return selectFilters(state, 'workouts', checked);
};

export const selectFiltersWorkoutsChecked = (state, filters) => {
  return selectFiltersChecked(state, 'workouts', filters);
};

export const selectFiltersWorkoutsGrouped = (state, checkedWorkouts) => {
  return groupBy(selectFiltersWorkouts(state, checkedWorkouts), 'type');
};

export const selectFiltersVideos = (state, params) => {
  return state.videos.map(filter => ({
    ...filter,
    checked: includes(params, filter.value),
  }));
};

export const selectFiltersVideosGrouped = (state, params) => {
  return groupBy(selectFiltersVideos(state, params), 'key');
};

export const selectFiltersVideosChecked = (state, params) => {
  return filter(selectFiltersVideos(state, params), 'checked');
};

// Helpers
export const isChecked = (key, array) => array.indexOf(key) >= 0;

export const setChecked = (item, key, checkedIds) => {
  return {
    ...item,
    checked: isChecked(item[key], checkedIds),
  };
};

export const toArray = filters => {
  if (typeof filters === 'string' && filters.length) {
    return filters.split(',').map(Number);
  }

  return [];
};

export const toString = filters => {
  return filters.join(',');
};

export const updateFilters = (id, type, params, list = []) => {
  const checkedIds = toArray(params);

  if (type === 'radio') {
    return toString(updateFiltersRadio(id, checkedIds, list));
  } else {
    return toString(updateFiltersCheckbox(id, checkedIds));
  }
};

export const updateFiltersRadio = (id, checkedIds, radioGroup = []) => {
  const inRadioGroup = filterId => find(radioGroup, ['id', filterId]);
  const filteredIds = reject(checkedIds, inRadioGroup);

  return isChecked(id, checkedIds)
    ? checkedIds.filter(i => i !== id)
    : [...filteredIds, id];
};

export const updateFiltersCheckbox = (id, checkedIds) => {
  return isChecked(id, checkedIds)
    ? checkedIds.filter(i => i !== id)
    : [...checkedIds, id];
};

// Action Creators
export const fetchFiltersRecipes = () => {
  return {
    type: FETCH,
    payload: {
      endpoint: '/v1/voome/recipe-categories',
      key: 'recipes',
    },
  };
};

export const fetchFiltersWorkouts = () => {
  return {
    type: FETCH,
    payload: {
      endpoint: '/v1/voome/workout-collections/filters',
      key: 'workouts',
    },
  };
};

export const toggleFiltersIsActive = () => {
  return { type: TOGGLE_IS_ACTIVE };
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH:
      return {
        ...state,
        loading: true,
        message: '',
      };

    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        [payload.key]: payload.data,
      };

    case FETCH_ERROR:
      return {
        ...state,
        loading: false,
        message: 'An error occured fetching the filters.',
      };

    case TOGGLE_IS_ACTIVE:
      return {
        ...state,
        isActive: !state.isActive,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/filters.js